import {useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import type {ProductDetail} from "@corporatesclub/shared";
import {formatPriceLabel, resolvePdpVolumeVariants} from "@corporatesclub/shared";
import {DsBadge, DsButton, DsPrice} from "@corporatesclub/ui";
import {useI18n} from "./I18nProvider";
import {PdpVolumeVariants} from "./PdpVolumeVariants";
import {useSession} from "./SessionProvider";
import {useShopSite} from "./ShopSiteProvider";

type Props = {
  product: ProductDetail;
  onAdd: (qty: number) => void | Promise<void>;
  isAdding: boolean;
  canAddToCart?: boolean;
  onWishlist?: (e: React.MouseEvent) => void | Promise<void>;
};

export function ProductBuyBox({product, onAdd, isAdding, canAddToCart = true, onWishlist}: Props) {
  const {t, currency, eurRate} = useI18n();
  const {user} = useSession();
  const {status, ready: siteReady} = useShopSite();
  const pricesHidden = product.pricesHidden || (siteReady && !user?.authenticated && !status.guestPricesVisible);
  const canOrder = user?.authenticated || (siteReady && status.guestOrdersAllowed);
  const showCommerce = canAddToCart && canOrder;
  const maxQty = Math.max(1, Math.min(product.stoc > 0 ? product.stoc : 99, 99));
  const [qty, setQty] = useState(1);
  const [stickyVisible, setStickyVisible] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);
  const priceLabel = (lei: number) => formatPriceLabel(lei, currency, eurRate, t("Lei"));
  const savings = useMemo(() => {
    const current = product.pretUnit;
    const base = product.pretBase;
    if(base <= 0 || current >= base) return null;
    const amount = Math.round((base - current) * 100) / 100;
    const percent = product.discountPercent > 0
      ? product.discountPercent
      : Math.round((amount / base) * 100);
    return {amount, percent, base};
  }, [product.discountPercent, product.pretBase, product.pretUnit]);
  const deliveryLabel = product.deliveryEstimate?.label || `${product.estimatedDelivery.dayName}, ${product.estimatedDelivery.dateShort}`;
  const currentPrice = priceLabel(product.pretUnit);
  const oldPrice = savings ? priceLabel(savings.base) : undefined;
  const saveLabel = savings
    ? `${t("You save")} ${priceLabel(savings.amount)} (${savings.percent}%)`
    : undefined;
  const addLabel = isAdding ? "..." : t("Add to cart");
  const volumeVariants = useMemo(() => resolvePdpVolumeVariants(product), [product]);

  useEffect(() => {
    setQty(1);
  }, [product.id]);
  useEffect(() => {
    if(qty > maxQty) setQty(maxQty);
  }, [qty, maxQty]);
  useEffect(() => {
    const el = actionsRef.current;
    if(!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setStickyVisible(!entry.isIntersecting),
      {root: null, rootMargin: "0px", threshold: 0}
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    document.body.classList.toggle("pdp-stickyVisible", stickyVisible);
    return () => document.body.classList.remove("pdp-stickyVisible");
  }, [stickyVisible]);

  function dec() { setQty((n) => Math.max(1, n - 1)); }
  function inc() { setQty((n) => Math.min(maxQty, n + 1)); }
  async function submit(e?: React.FormEvent | React.MouseEvent) {
    e?.preventDefault();
    if(isAdding || !canAddToCart) return;
    await onAdd(qty);
  }

  const stickyBar = stickyVisible && showCommerce && !pricesHidden ? createPortal(
    <div className="bx-pdpStickyBar isVisible" role="region" aria-label={t("Add to cart")}>
      <div className="bx-pdpStickyBar__price">
        <DsPrice current={currentPrice} old={oldPrice} sale={!!savings} />
      </div>
      <DsButton variant="primary" size="md" onClick={submit} disabled={isAdding}>
        {addLabel}
      </DsButton>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <div className="pvBuyBox bx-buyBox">
        <div className="bx-buyBox__meta">
          <DsBadge variant="success"><i className="fa fa-check" aria-hidden="true" /> {t("Available")}</DsBadge>
          {product.freeShipping ? (
            <DsBadge variant="primary"><i className="fa fa-truck" aria-hidden="true" /> {t("Free shipping title")}</DsBadge>
          ) : null}
        </div>
        <p className="bx-deliveryHint">
          <i className="fa fa-calendar" aria-hidden="true" />
          {t("Expected delivery")}: {deliveryLabel}
        </p>
        <PdpVolumeVariants variants={volumeVariants} pricesHidden={pricesHidden} />
        {!pricesHidden ? (
        <div className="bx-buyBox__priceBlock">
          {savings ? <span className="bx-buyBox__offerLabel">{t("Special offer")}</span> : null}
          <DsPrice
            className="bx-buyBox__priceRow"
            current={currentPrice}
            old={oldPrice}
            saveLabel={saveLabel}
            sale={!!savings}
          />
        </div>
        ) : (
          <p className="bx-buyBox__priceHidden">{t("Sign in to see price")}</p>
        )}
        <div ref={actionsRef} className="pvBuyRow bx-buyBox__actions">
          {showCommerce ? (
          <div className="pvBuyQty bx-buyBox__qty" role="group" aria-label={t("Qty")}>
            <button type="button" className="pvBuyQtyBtn" aria-label="-" onClick={dec} disabled={qty <= 1}>−</button>
            <span className="pvBuyQtyVal">{qty}</span>
            <button type="button" className="pvBuyQtyBtn" aria-label="+" onClick={inc} disabled={qty >= maxQty}>+</button>
          </div>
          ) : null}
          {showCommerce ? (
          <DsButton variant="primary" size="md" className="pvBuyBtn" onClick={submit} disabled={isAdding}>
            {addLabel}
          </DsButton>
          ) : null}
          {onWishlist ? (
            <button
              type="button"
              className={`pvBuyWishBtn bx-buyBox__wish pvBuyWishBtn--mobile anim${product.inWishlist ? " isActive" : ""}`}
              aria-label="Wishlist"
              aria-pressed={!!product.inWishlist}
              onClick={onWishlist}
            >
              <i className={`fa${product.inWishlist ? " fa-heart" : " fa-heart-o"}`} aria-hidden="true" />
            </button>
          ) : null}
        </div>
        <div className="bx-buyBox__trust">
          <span><i className="fa fa-truck" /> {t("Fast delivery")}</span>
          <span><i className="fa fa-lock" /> {t("Secure payment")}</span>
          <span><i className="fa fa-star" /> {t("Premium Brands")}</span>
        </div>
      </div>
      {stickyBar}
    </>
  );
}
